import { ItemList } from "../orm";
import { get, chunk, head, size, map, filter, forEach } from "lodash";
import moment from "moment";
import { upload_to_aws_directly } from "../lib/image";
import { postBlob } from "../lib/http";
import { photographList } from "./photograph";

const MAP_PLACEHOLDER_IMAGE_URL = null;
const PROCESSING_BATCH_SIZE = 10;
export const DEFAULT_NUM_TEMS_PER_GALLERY_PAGE = 25;

const logger = {
  log: (...args) => console.log("GalleryList", ...args),
  error: (...args) => console.error("GalleryList", ...args),
};

export class GalleryList extends ItemList {
  getEntityKey() {
    return "photographer/gallery";
  }

  invalidateGalleryAndPhotographs(gallery_id) {
    return (dispatch, getState) => {
      const gallery = this.getObject(gallery_id);
      forEach(gallery.photographs, (photograph) => {
        dispatch(photographList.invalidateObject(photograph.id));
        dispatch(photographList.ensureObjectLoaded(photograph.id));
      });
      dispatch(this.invalidateObject(gallery_id));
      dispatch(this.ensureObjectLoaded(gallery_id));
    };
  }

  getGalleryAvatarUrl(gallery) {
    const photograph_id =
      gallery.map_photograph || get(head(gallery.photographs), "id");
    if (!photograph_id) {
      return MAP_PLACEHOLDER_IMAGE_URL;
    }
    const photograph = head(
      filter(gallery.photographs, (x) => x.id === photograph_id),
    );
    if (!photograph) {
      return MAP_PLACEHOLDER_IMAGE_URL;
    }
    const download_url = get(
      photograph,
      ["avatar_size_file_info", "download_url"],
      MAP_PLACEHOLDER_IMAGE_URL,
    );
    return download_url;
  }

  async processFileUpload({
    file,
    gallery_id,
    gallery,
    gallery_photographs,
    country_code,
    onFileProcessed,
    onFileUploadingProgress,
  }) {
    const is_duplicate =
      size(
        map(
          filter(
            gallery_photographs,
            (photograph) => photograph.photo_name === get(file, "name"),
          ),
        ),
      ) > 0;

    if (is_duplicate) {
      onFileUploadingProgress(file, "duplicate");
      onFileProcessed(file, null);
      return;
    }

    onFileUploadingProgress(file, "scaling");
    // const scaledFile = await scaleImageFile(file, highResOptions)
    const scaledFile = file;
    onFileUploadingProgress(file, "uploading");

    let awsResult;
    try {
      awsResult = await upload_to_aws_directly(scaledFile, country_code);
    } catch (error) {
      onFileProcessed(file, error);
      return;
    }

    const { s3_filepath } = awsResult;

    const blobData = new window.FormData();
    blobData.append("aws_path", s3_filepath);
    blobData.append("filename", scaledFile.name);
    blobData.append("gallery", gallery_id);
    blobData.append("content_type", scaledFile.type);
    blobData.append("country_code", country_code);
    blobData.append(
      "photograph_taken_at",
      moment(file.lastModifiedDate).format(),
    );

    try {
      const [json, response, isSuccessful] = await postBlob(
        "photographer/photograph",
        blobData,
      );
      if (isSuccessful) {
        onFileProcessed(file, null);
      } else {
        onFileProcessed(file, {
          reason: "photograph_api_failed",
          error: response,
        });
      }
    } catch (error) {
      onFileProcessed(file, { reason: "photograph_api_error", error });
    }
  }

  savePhotos(props) {
    const {
      files,
      gallery_id,
      gallery,
      gallery_photographs,
      country_code,
      on_file_upload_progress: onFileUploadingProgress,
    } = props;

    const successes = [];
    const failures = [];

    // logger.log("savePhotos", props);

    const promise = new Promise(async (resolve, reject) => {
      map(files, (file) => {
        onFileUploadingProgress(file, "pending");
      });

      const onFileProcessed = (file, errorMessage) => {
        if (!errorMessage) {
          successes.push(file);
          onFileUploadingProgress(file, "success");
          // logger.log("DONE", file, errorMessage);
        } else {
          failures.push(file);
          onFileUploadingProgress(file, "failed", errorMessage);
          logger.error("FAILED", file, errorMessage);
        }

        if (size(successes) + size(failures) === size(files)) {
          resolve({ successes, failures });
        }
      };

      const fileBatches = chunk(files, PROCESSING_BATCH_SIZE);

      let batchCursor = fileBatches.length;

      const _processFileUpload = this.processFileUpload;

      function processBatch() {
        if (batchCursor > 0) {
          batchCursor--;
        } else {
          return;
        }
        const fileBatch = fileBatches[batchCursor];
        // logger.log(`PROCESSING BATCH ${batchCursor}`, fileBatch);
        Promise.allSettled(
          fileBatch.map((file) =>
            _processFileUpload({
              file,
              gallery_id,
              gallery,
              gallery_photographs,
              country_code,
              onFileProcessed,
              onFileUploadingProgress,
            }),
          ),
        ).then(() => {
          processBatch();
        });
      }

      processBatch();
    });

    return promise;
  }
}

export const galleryList = new GalleryList("gallery_default");
